import React, { useEffect, useMemo, useState } from "react";
import {
  FaCloudUploadAlt,
  FaCog,
  FaDownload,
  FaExchangeAlt,
  FaFileAlt,
  FaGlobeAmericas,
  FaPlus,
  FaSearch,
  FaStore,
  FaUserTie,
  FaWallet,
} from "react-icons/fa";
import "../styles/ReportingPage.css";
import AppDropdown from "../components/AppDropdown";
import * as XLSX from "xlsx";
import { FaCheck } from "react-icons/fa";
import {
  getReportSummary,
  getReportVolumeTrends,
  getReportRecentActivity,
  getPortalMerchantsList,
  listOrgAgents,
  getOrgRecentTransactions,
} from "../api/api";
import { getOrgId, getUserData } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";
import useSidebarAction from "../hooks/useSidebarAction";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const pick = (res) => res?.data ?? res ?? null;
const asArray = (raw) =>
  (Array.isArray(raw) && raw) ||
  (Array.isArray(raw?.data) && raw.data) ||
  (Array.isArray(raw?.categories) && raw.categories) ||
  (Array.isArray(raw?.items) && raw.items) ||
  (Array.isArray(raw?.reports) && raw.reports) ||
  [];

/* Fixed category shells; live stats are merged in from the API by type keyword. */
const DEFAULT_CATEGORIES = [
  { key: "merchant", title: "Merchant Reports", subtitle: "Settlement reconciliations & fee audits", icon: <FaStore /> },
  { key: "agent", title: "Agent Reports", subtitle: "Commission tracking & performance", icon: <FaUserTie /> },
  { key: "transaction", title: "Transactions", subtitle: "Full transaction history & raw data", icon: <FaExchangeAlt /> },
  { key: "settlement", title: "Settlements", subtitle: "Payout cycles & bank transfers", icon: <FaWallet /> },
];

/* Pull the row array out of whatever envelope an API returns */
const rowsFrom = (res) => {
  const p = res?.data ?? res;
  return (
    (Array.isArray(p) && p) ||
    (Array.isArray(p?.merchants) && p.merchants) ||
    (Array.isArray(p?.agents) && p.agents) ||
    (Array.isArray(p?.transactions) && p.transactions) ||
    (Array.isArray(p?.data) && p.data) ||
    (Array.isArray(p?.items) && p.items) ||
    []
  );
};

const totalFrom = (res, fallback = 0) => {
  const p = res?.data ?? res;
  return (
    num(
      res?.total ?? res?.totalCount ?? res?.count ??
      p?.total ?? p?.totalCount ?? p?.count ??
      res?.pagination?.total ?? p?.pagination?.total ??
      p?.pagination?.totalRecords
    ) || fallback
  );
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

/* Fetch every page of a list endpoint (capped) and flatten into one array */
const PAGE_LIMIT = 100;
const MAX_PAGES = 50;
const fetchAllPages = async (fetcher) => {
  const first = await fetcher(1);
  let out = rowsFrom(first);
  const total = totalFrom(first, out.length);
  const totalPages = Math.min(MAX_PAGES, Math.max(1, Math.ceil(total / PAGE_LIMIT)));
  for (let p = 2; p <= totalPages; p++) {
    // eslint-disable-next-line no-await-in-loop
    const next = await fetcher(p);
    const chunk = rowsFrom(next);
    if (!chunk.length) break;
    out = out.concat(chunk);
  }
  return out;
};

/* Report definitions: which endpoint to page through + how to shape each row.
   `opts.dateRange` comes from the modal's Time Range selector. */
const REPORT_BUILDERS = {
  merchant: {
    fetch: (orgId, page) => getPortalMerchantsList({ orgId, page, limit: PAGE_LIMIT, tab: "all", category: "all", region: "all", sort: "newest" }),
    map: (m) => ({
      "Merchant ID": m.merchantID || m.MID || m._id || "—",
      "Merchant Name": m.merchantName || m.name || "—",
      Category: m.categoryName || m.category || "—",
      Region: m.region || m.country || "—",
      "Assigned Agent": m.assignedAgent || m.agentName || m.agent || "—",
      Status: m.status || "—",
      "Static QR": num(m.staticQr ?? m.staticQrCount),
      "Dynamic QR": num(m.dynamicQr ?? m.dynamicQrCount),
      Volume: num(m.volume ?? m.totalVolume ?? m.monthlyVolume),
      "Last Activity": fmtDate(m.lastActivity ?? m.lastTxnAt ?? m.updatedAt),
    }),
  },
  agent: {
    fetch: (orgId, page, opts = {}) => listOrgAgents({ orgId, page, limit: PAGE_LIMIT, dateRange: opts.dateRange || "last30days", status: "all" }),
    map: (a, i) => ({
      "Agent ID": a.agentId || a.code || a._id || `AG-${i + 1}`,
      "Agent Name": a.name || a.agentName || "—",
      Region: a.region || a.area || "—",
      Status: a.status || "—",
      Merchants: num(a.merchants ?? a.totalMerchants ?? a.merchantCount),
      Volume: num(a.volume ?? a.totalVolume ?? a.monthlyVolume),
      Commission: num(a.commission ?? a.totalCommission ?? a.commissionEarned),
      "Due Payout": num(a.payout ?? a.duePayout ?? a.pendingPayout),
      "Last Active": fmtDate(a.lastActive ?? a.lastActiveAt ?? a.updatedAt),
    }),
  },
  transaction: {
    fetch: (orgId, page, opts = {}) => getOrgRecentTransactions({ orgId, page, limit: PAGE_LIMIT, dateRange: opts.dateRange || "last30days" }),
    map: (t, i) => ({
      "Transaction ID": t.transactionId || t.txnId || t.txId || t.id || `TXN-${i + 1}`,
      Merchant: t.merchant || t.merchantName || "—",
      Agent: t.agent || t.agentName || t.agentId || "—",
      "QR Type": t.qrType || t.type || "—",
      Amount: num(t.amount ?? t.volume),
      Status: t.status || "—",
      Settlement: t.settlement || t.settlementStatus || "—",
      Date: fmtDate(t.date ?? t.time ?? t.createdAt),
    }),
  },
};
/* Settlements reuse the transaction feed */
REPORT_BUILDERS.settlement = {
  fetch: REPORT_BUILDERS.transaction.fetch,
  map: (t, i) => ({
    "Transaction ID": t.transactionId || t.txnId || t.id || `TXN-${i + 1}`,
    Merchant: t.merchant || t.merchantName || "—",
    Amount: num(t.amount ?? t.volume),
    "Settlement Status": t.settlement || t.settlementStatus || "Pending",
    Date: fmtDate(t.date ?? t.time ?? t.createdAt),
  }),
};

const builderKey = (key = "") => {
  const k = String(key).toLowerCase();
  if (k.includes("agent")) return "agent";
  if (k.includes("settle")) return "settlement";
  if (k.includes("transaction")) return "transaction";
  return "merchant";
};

/* Last-run timestamps recorded locally when a report is generated
   (the summary API doesn't provide them) */
const LAST_RUN_KEY = "rp-last-run";
const readLastRuns = () => {
  try {
    return JSON.parse(localStorage.getItem(LAST_RUN_KEY)) || {};
  } catch {
    return {};
  }
};
const recordLastRun = (typeKey) => {
  const map = readLastRuns();
  map[builderKey(typeKey)] = new Date().toISOString();
  try {
    localStorage.setItem(LAST_RUN_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable — skip */
  }
};

/* Fetch real data for a report type and return shaped rows */
const buildReportRows = async (typeKey, orgId, opts = {}) => {
  const def = REPORT_BUILDERS[builderKey(typeKey)];
  const raw = await fetchAllPages((page) => def.fetch(orgId, page, opts));
  return raw.map((row, i) => def.map(row, i));
};

/* Lightweight count for the preview panel (first page only) */
const countReportRecords = async (typeKey, orgId, opts = {}) => {
  const def = REPORT_BUILDERS[builderKey(typeKey)];
  const first = await def.fetch(orgId, 1, opts);
  return totalFrom(first, rowsFrom(first).length);
};

const escapeHtml = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/* PDF: open a print-friendly window with the data table and trigger the print/save dialog */
const downloadPdf = (rows, fileName) => {
  const cols = Object.keys(rows[0] || {});
  const title = fileName.replace(/\.(pdf|xlsx|csv)$/i, "");
  const thead = cols.map((c) => `<th>${escapeHtml(c)}</th>`).join("");
  const tbody = rows
    .map((r) => `<tr>${cols.map((c) => `<td>${escapeHtml(r[c])}</td>`).join("")}</tr>`)
    .join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
    <style>
      *{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}
      body{margin:24px;color:#0f172a}
      h1{font-size:18px;margin:0 0 4px}
      .meta{font-size:11px;color:#64748b;margin-bottom:16px}
      table{width:100%;border-collapse:collapse;font-size:10px}
      th,td{border:1px solid #e2e8f0;padding:6px 8px;text-align:left}
      thead th{background:#f1f5f9;text-transform:uppercase;font-size:9px;letter-spacing:.04em}
      tbody tr:nth-child(even){background:#f8fafc}
      @page{margin:14mm}
    </style></head><body>
    <h1>${escapeHtml(title)}</h1>
    <div class="meta">${rows.length} records &middot; Generated ${new Date().toLocaleString()}</div>
    <table><thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody></table>
    <script>window.onload=function(){window.focus();window.print();};</script>
    </body></html>`;
  const win = window.open("", "_blank");
  if (!win) return false; // popup blocked
  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
};

/* Turn shaped rows into a downloadable file based on the chosen format (csv / xlsx / pdf) */
const downloadRows = (rows, fileName, format) => {
  if (format === "pdf") {
    return downloadPdf(rows, fileName);
  }
  const ws = XLSX.utils.json_to_sheet(rows);
  if (format === "csv") {
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName.replace(/\.(xlsx|pdf)$/i, ".csv");
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return true;
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");
  XLSX.writeFile(wb, fileName.replace(/\.(pdf|csv)$/i, ".xlsx"));
  return true;
};

const Analytics = ({ isSidebarOpen }) => {
  const { showSuccess, showError } = useToast();
  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const userType = String(user?.user_type || user?.type || "").toLowerCase();
  const isSuperAdmin = userType === "admin";
  const orgId = getOrgId() || user?.profile?.orgId || user?.orgId || user?.organizationId || null;

  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);
  const [apiCategories, setApiCategories] = useState([]);
  const [volumeBars, setVolumeBars] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [volumeMeta, setVolumeMeta] = useState({ label: "Last 7 Days", change: "0%" });
  const [recentRows, setRecentRows] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  /* Configure Export modal (New Report) */
  const [exportOpen, setExportOpen] = useState(false);

  /* Sidebar dropdown action → open the same "New Report" export modal */
  useSidebarAction({ new: () => setExportOpen(true) });

  const [expType, setExpType] = useState("transaction");
  const [expMerchant, setExpMerchant] = useState("all");
  const [expRange, setExpRange] = useState("last30days");
  const [expFormat, setExpFormat] = useState("csv");
  const [expRecurring, setExpRecurring] = useState(false);
  const [expBusy, setExpBusy] = useState(false);
  const [expDone, setExpDone] = useState(null); // { fileName, url, rows } after a successful generate
  const [expCount, setExpCount] = useState(null); // live record count for the selected report type

  useEffect(() => {
    if (!orgId || isSuperAdmin) return;
    let cancelled = false;
    const run = async () => {
      const results = await Promise.allSettled([
        getReportSummary(orgId),
        getReportVolumeTrends(orgId),
        getReportRecentActivity(orgId, 1, 10),
      ]);
      if (cancelled) return;
      const val = (i) => (results[i].status === "fulfilled" ? pick(results[i].value) : null);

      const sum = val(0) || {};
      setSummary(sum);
      setApiCategories(asArray(sum.categories ?? sum));

      // Volume trends → bar heights (normalized 0..100)
      const trendRaw = val(1);
      const trendArr = asArray(trendRaw).map((d) =>
        typeof d === "number" ? d : num(d?.value ?? d?.amount ?? d?.volume ?? d?.total)
      );
      const maxT = Math.max(...trendArr, 0);
      setVolumeBars(trendArr.length ? trendArr.map((v) => (maxT ? Math.round((v / maxT) * 100) : 0)) : [0, 0, 0, 0, 0, 0, 0]);
      setVolumeMeta({
        label: trendRaw?.label ?? trendRaw?.range ?? "Last 7 Days",
        change: trendRaw?.change ?? trendRaw?.growth ?? "0%",
      });

      // Recent activity rows
      setRecentRows(
        asArray(val(2)).map((r, idx) => ({
          id: r.id ?? r._id ?? `${r.name ?? "report"}-${idx}`,
          name: r.name ?? r.reportName ?? r.fileName ?? "Report",
          type: r.type ?? r.category ?? "—",
          status: String(r.status ?? "Ready"),
          rawDate: r.date ?? r.createdAt ?? r.generatedAt ?? null,
          date: fmtDate(r.date ?? r.createdAt ?? r.generatedAt),
        }))
      );
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [orgId, refreshKey, isSuperAdmin]);

  /* Merge live stats onto the fixed category shells */
  const categories = useMemo(() => {
    return DEFAULT_CATEGORIES.map((cat) => {
      const match = apiCategories.find((c) => {
        const t = String(c?.type ?? c?.key ?? c?.name ?? c?.title ?? "").toLowerCase();
        return t.includes(cat.key);
      });
      /* Latest matching entry from Recent Activity, if any */
      const activityDate = recentRows
        .filter((r) => String(r.type).toLowerCase().includes(cat.key) && r.rawDate)
        .map((r) => new Date(r.rawDate).getTime())
        .filter((t) => !Number.isNaN(t))
        .sort((a, b) => b - a)[0];
      const lastRunRaw =
        readLastRuns()[cat.key] ??
        match?.lastRun ?? match?.lastGenerated ?? match?.lastRunAt ?? match?.updatedAt ??
        (activityDate ? new Date(activityDate).toISOString() : null) ?? null;
      return {
        ...cat,
        lastRun: lastRunRaw ? fmtDate(lastRunRaw) : "—",
        avgSize: match?.avgSize ?? match?.averageSize ?? "0 MB",
      };
    });
  }, [apiCategories, recentRows, refreshKey]);

  const metrics = [
    { title: "Total Exports", value: String(num(summary?.totalExports ?? summary?.exports ?? summary?.totalReports)), icon: <FaFileAlt />, meta: "Files generated" },
    { title: "Scheduled", value: String(num(summary?.scheduled ?? summary?.scheduledJobs ?? summary?.automations)), icon: <FaCog />, meta: "Automations active" },
    {
      title: "Storage Used",
      value: `${num(summary?.storageUsed)}GB/${num(summary?.storageLimit ?? 5)}GB`,
      icon: <FaCloudUploadAlt />,
      meta: `${num(summary?.utilization ?? summary?.storagePercent)}% utilization`,
      progress: num(summary?.utilization ?? summary?.storagePercent),
    },
  ];

  const filteredRows = useMemo(
    () =>
      recentRows.filter((row) =>
        `${row.name} ${row.type} ${row.status}`.toLowerCase().includes(search.toLowerCase())
      ),
    [recentRows, search]
  );

  const handleGenerate = async (cat) => {
    if (!orgId) {
      showError("Error", "No organization selected", 3000);
      return;
    }
    if (busyKey) return;
    try {
      setBusyKey(cat.key);
      const rows = await buildReportRows(cat.key, orgId);
      if (!rows.length) {
        showError("Report", "No data available for this report", 3000);
        return;
      }
      const fileName = `${cat.key}-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadRows(rows, fileName, "xlsx");
      recordLastRun(cat.key);
      showSuccess("Report", `${cat.title} generated (${rows.length} rows)`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Failed to generate report", 3000);
    } finally {
      setBusyKey(null);
    }
  };

  /* Recent Activity: re-pull the data for that report type and download the file */
  const [downloadingId, setDownloadingId] = useState(null);
  const handleActivityDownload = async (row) => {
    if (!orgId || downloadingId) return;
    try {
      setDownloadingId(row.id);
      const rows = await buildReportRows(row.type, orgId);
      if (!rows.length) {
        showError("Download", "No data available for this report", 3000);
        return;
      }
      const ext = /\.(csv|pdf)$/i.exec(row.name)?.[1]?.toLowerCase() || "xlsx";
      const fileName = /\.(xlsx|csv|pdf)$/i.test(row.name)
        ? row.name
        : `${row.name}.${ext}`;
      downloadRows(rows, fileName, ext);
      showSuccess("Download", `${fileName} downloaded (${rows.length} rows)`);
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Failed to download report", 3000);
    } finally {
      setDownloadingId(null);
    }
  };

  /* Populate the Export Preview record count from the live list API */
  useEffect(() => {
    if (!exportOpen || !orgId || expDone) return;
    let cancelled = false;
    setExpCount(null);
    countReportRecords(expType, orgId, { dateRange: expRange })
      .then((n) => !cancelled && setExpCount(n))
      .catch(() => !cancelled && setExpCount(0));
    return () => {
      cancelled = true;
    };
  }, [exportOpen, expType, expRange, orgId, expDone]);

  /* Generate from the Configure Export modal — pulls live data, shows the Export Complete card */
  const handleConfigureGenerate = async () => {
    if (!orgId) {
      showError("Error", "No organization selected", 3000);
      return;
    }
    if (expBusy) return;
    try {
      setExpBusy(true);
      const rows = await buildReportRows(expType, orgId, { dateRange: expRange });
      if (!rows.length) {
        showError("Report", "No data available for this report", 3000);
        return;
      }
      const ext = expFormat === "xlsx" ? "xlsx" : expFormat === "pdf" ? "pdf" : "csv";
      const fileName = `${expType}-report-${new Date().toISOString().slice(0, 10)}.${ext}`;
      recordLastRun(expType);
      showSuccess("Report", `Report generated (${rows.length} rows)`);
      setExpDone({ rows, fileName, format: expFormat });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Failed to generate report", 3000);
    } finally {
      setExpBusy(false);
    }
  };

  /* Download the generated report file (built from the live data pulled at generate time) */
  const handleDownloadReport = () => {
    if (!expDone) return;
    if (!expDone.rows?.length) {
      showError("Download", "No data available to download", 3000);
      return;
    }
    const ok = downloadRows(expDone.rows, expDone.fileName, expDone.format);
    if (ok === false) {
      showError("Download", "Allow pop-ups to export the PDF, then try again", 3500);
    }
  };

  const closeExportModal = () => {
    setExportOpen(false);
    setExpDone(null);
  };

  /* Preview panel: live count + derived column/size estimates for the selected type */
  const expRecords = expCount == null ? null : num(expCount);
  const expColumns = Object.keys(REPORT_BUILDERS[builderKey(expType)].map({}, 0)).length;
  const estSizeLabel = (() => {
    if (expRecords == null) return "…";
    const bytes = expRecords * expColumns * 12; // rough per-cell estimate
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
  })();

  /* Reporting is org-scoped; super admins see a placeholder for now */
  if (isSuperAdmin) {
    return (
      <div className={`rp-page${isSidebarOpen ? " shifted" : ""}`} style={{ padding: 20 }}>
        <article className="rp-card rp-side-card" style={{ minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center" }}>
            <h3 style={{ margin: 0 }}>Analytics</h3>
            <p style={{ opacity: 0.8, marginTop: 8 }}>Coming soon…</p>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className={`rp-page${isSidebarOpen ? " shifted" : ""}`}>
      <div className="rp-topbar">
        <div className="rp-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search reports..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="rp-metrics">
        {metrics.map((item) => (
          <article className="rp-card rp-metric" key={item.title}>
            <div className="rp-metric__icon">{item.icon}</div>
            <div>
              <small>{item.title}</small>
              <strong>{item.value}</strong>
              {item.progress ? (
                <div className="rp-progress">
                  <span style={{ width: `${item.progress}%` }} />
                </div>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      <div className="rp-grid">
        <section className="rp-main">
          <div className="rp-section-head">
            <h3>Report Categories</h3>
          </div>

          <div className="rp-categories">
            {categories.map((card) => (
              <article key={card.title} className="rp-card rp-category">
                <div className="rp-category__head">
                  <div>
                    <h4>{card.title}</h4>
                    <p>{card.subtitle}</p>
                  </div>
                  <span>{card.icon}</span>
                </div>
                <div className="rp-category__stats">
                  <div>
                    <small>Last Run</small>
                    <strong>{card.lastRun}</strong>
                  </div>
                  <div>
                    <small>Avg Size</small>
                    <strong>{card.avgSize}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  className="rp-generate"
                  disabled={busyKey === card.key}
                  onClick={() => handleGenerate(card)}
                >
                  {busyKey === card.key ? "Generating…" : "Generate Report"}
                </button>
              </article>
            ))}
          </div>

          <article className="rp-card rp-activity">
            <div className="rp-activity__head">
              <h3>Recent Activity</h3>
              <button type="button">View all</button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Report Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: 24, opacity: 0.6 }}>
                      No reports found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>{row.type}</td>
                      <td>
                        <span className={`rp-badge rp-badge--${row.status.toLowerCase()}`}>{row.status}</span>
                      </td>
                      <td>{row.date}</td>
                      <td>
                        <button
                          type="button"
                          className="rp-action-btn"
                          title="Download"
                          disabled={downloadingId === row.id}
                          style={downloadingId === row.id ? { opacity: 0.5, cursor: "wait" } : undefined}
                          onClick={() => handleActivityDownload(row)}
                        >
                          <FaDownload />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </article>
        </section>

        <aside className="rp-side">
          <button type="button" className="rp-new-btn" onClick={() => setExportOpen(true)}>
            <FaPlus /> New Report
          </button>

          <article className="rp-card rp-side-card">
            <h4>Volume Trends</h4>
            <div className="rp-trend-bars">
              {volumeBars.map((bar, idx) => (
                <span key={idx} style={{ height: `${bar}%` }} />
              ))}
            </div>
            <div className="rp-trend-meta">
              <small>{volumeMeta.label}</small>
              <strong>{volumeMeta.change}</strong>
            </div>
          </article>

          <article className="rp-card rp-map">
            <FaGlobeAmericas />
            <h5>Live Global Activity</h5>
            <p>Real-time sync active</p>
          </article>
        </aside>
      </div>

      {exportOpen ? (
        <div className="rpx-overlay" role="dialog" aria-modal="true">
          <button type="button" className="rpx-scrim" aria-label="Close" onClick={closeExportModal} />
          {expDone ? (
            <div className="rpx-donecard">
              <div className="rpx-donecheck"><FaCheck /></div>
              <h3>Export Complete</h3>
              <p>Download Ready: {expDone.fileName}</p>
              <button type="button" className="rpx-download" onClick={handleDownloadReport}>
                Download File
              </button>
              <button type="button" className="rpx-return" onClick={closeExportModal}>
                Return to Dashboard
              </button>
            </div>
          ) : (
          <div className="rpx-modal">
            <div className="rpx-config">
              <div className="rpx-head">
                <div>
                  <h3>Configure Export</h3>
                  <p>Tailor your data export parameters</p>
                </div>
                <button type="button" className="rpx-close" aria-label="Close" onClick={() => setExportOpen(false)}>×</button>
              </div>

              <div className="rpx-sectionlabel"><span>1</span> Report Configuration</div>
              <div className="rpx-fieldrow">
                <div className="rpx-field">
                  <label>Report Type</label>
                  <AppDropdown
                    className="appdd--block"
                    options={[
                      { value: "transaction", label: "Transaction Logs" },
                      { value: "merchant", label: "Merchant Reports" },
                      { value: "agent", label: "Agent Reports" },
                      { value: "settlement", label: "Settlements" },
                    ]}
                    value={expType}
                    onChange={setExpType}
                  />
                </div>
                <div className="rpx-field">
                  <label>Merchants</label>
                  <AppDropdown
                    className="appdd--block"
                    options={[{ value: "all", label: "All Merchants" }]}
                    value={expMerchant}
                    onChange={setExpMerchant}
                  />
                </div>
              </div>
              <div className="rpx-field">
                <label>Time Range</label>
                <AppDropdown
                  className="appdd--block"
                  options={[
                    { value: "today", label: "Today" },
                    { value: "last7days", label: "Last 7 Days" },
                    { value: "last30days", label: "Last 30 Days" },
                    { value: "last90days", label: "Last 90 Days" },
                  ]}
                  value={expRange}
                  onChange={setExpRange}
                />
              </div>

              <div className="rpx-sectionlabel"><span>2</span> Format Selection</div>
              <div className="rpx-formats">
                {["csv", "xlsx", "pdf"].map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={expFormat === f ? "rpx-format active" : "rpx-format"}
                    onClick={() => setExpFormat(f)}
                  >
                    <FaFileAlt />
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>

              <label className="rpx-recurring">
                <span>
                  <strong>Recurring Schedule</strong>
                  <small>Automate this export weekly</small>
                </span>
                <input
                  type="checkbox"
                  checked={expRecurring}
                  onChange={(e) => setExpRecurring(e.target.checked)}
                />
                <i className="rpx-switch" />
              </label>

              <div className="rpx-footer">
                <button type="button" className="rpx-cancel" onClick={() => setExportOpen(false)}>Cancel</button>
                <button type="button" className="rpx-generate" onClick={handleConfigureGenerate} disabled={expBusy}>
                  {expBusy ? "Generating..." : "Generate Report"}
                </button>
              </div>
            </div>

            <div className="rpx-preview">
              <h4>Export Preview</h4>
              <div className="rpx-size">
                <small>Estimated Size</small>
                <strong>{estSizeLabel}</strong>
              </div>
              <div className="rpx-statline"><span>Total Records</span><strong>{expRecords == null ? "…" : expRecords.toLocaleString()}</strong></div>
              <div className="rpx-statline"><span>Columns</span><strong>{expColumns}</strong></div>
              <div className="rpx-statline"><span>Data Density</span><strong>{summary?.dataDensity ?? "High"}</strong></div>
              <div className="rpx-note">
                Generating high-volume transaction logs may take up to 45 seconds. You will be notified when ready.
              </div>
              <div className="rpx-stream">Live Data Stream</div>
            </div>
          </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default Analytics;
